'use client'

import { SiFacebook, SiGithub, SiInstagram, SiWhatsapp } from '@icons-pack/react-simple-icons'
import { useTranslations } from '@tszhong0411/i18n/client'
import { Button } from '@tszhong0411/ui/components/button'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@tszhong0411/ui/components/command'
import { CodeIcon, CommandIcon, LinkIcon } from 'lucide-react'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { FaLinkedin } from 'react-icons/fa'

import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard'
import { SITE_FACEBOOK_URL, SITE_GITHUB_URL, SITE_INSTAGRAM_URL } from '@/lib/constants'

const CommandMenu = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [copy] = useCopyToClipboard()
  const t = useTranslations()

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setIsOpen((value) => !value)
      }
    }

    document.addEventListener('keydown', down)

    return () => document.removeEventListener('keydown', down)
  }, [])

  const openLink = useCallback((url: string) => {
    setIsOpen(false)
    window.open(url, '_blank', 'noopener')
  }, [])

  const groups: Array<{
    name: string
    actions: Array<{
      title: string
      icon: React.ReactNode
      onSelect: () => void | Promise<void>
    }>
  }> = [
    {
      name: t('command-menu.groups.general'),
      actions: [
        {
          title: t('command-menu.actions.copy-link'),
          icon: <LinkIcon />,
          onSelect: async () => {
            setIsOpen(false)

            await copy({ text: globalThis.location.href })
          }
        },
        {
          title: t('command-menu.actions.source-code'),
          icon: <CodeIcon />,
          onSelect: () => openLink('https://github.com/UserAAR/devaar.me')
        }
      ]
    },
    {
      name: t('command-menu.groups.social'),
      actions: [
        {
          title: 'GitHub',
          icon: <SiGithub />,
          onSelect: () => openLink(SITE_GITHUB_URL)
        },
        {
          title: 'LinkedIn',
          icon: <FaLinkedin />,
          onSelect: () => openLink('https://www.linkedin.com/in/aar-amil/')
        },
        {
          title: 'Instagram',
          icon: <SiInstagram />,
          onSelect: () => openLink(SITE_INSTAGRAM_URL)
        },
        {
          title: 'Facebook',
          icon: <SiFacebook />,
          onSelect: () => openLink(SITE_FACEBOOK_URL)
        },
        {
          title: 'WhatsApp',
          icon: <SiWhatsapp />,
          onSelect: () =>
            openLink('https://api.whatsapp.com/send/?phone=994507746585&text=Salam,Amil!')
        }
      ]
    }
  ]

  return (
    <>
      <Button
        variant='ghost'
        className='size-9 p-0'
        onClick={() => setIsOpen(true)}
        aria-label={t('command-menu.open-menu')}
      >
        <CommandIcon />
      </Button>
      <CommandDialog open={isOpen} onOpenChange={setIsOpen}>
        <CommandInput placeholder={t('command-menu.placeholder')} />
        <CommandList>
          <CommandEmpty>{t('command-menu.no-results')}</CommandEmpty>
          {groups.map((group, i) => (
            <Fragment key={group.name}>
              <CommandGroup heading={group.name}>
                {group.actions.map((action) => (
                  <CommandItem key={action.title} onSelect={action.onSelect}>
                    {action.icon}
                    {action.title}
                  </CommandItem>
                ))}
              </CommandGroup>
              {i === groups.length - 1 ? null : <CommandSeparator />}
            </Fragment>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  )
}

export default CommandMenu
